import { type AnchorHTMLAttributes, type ReactNode } from 'react';
import SvgIcon from '@liquidweb-dev/undercurrent/SvgIcon';
import Typography, {
	type TypographyProps,
} from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export interface NavLinkMainProps extends TypographyProps {
	active?: boolean;
	startIcon?: ReactNode;
	endAdornment?: ReactNode;
	href?: AnchorHTMLAttributes<HTMLAnchorElement>['href'];
	target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
	chip?: ReactNode;
}

export function NavLinkMain({
	active = false,
	startIcon,
	endAdornment,
	sx: sxProp,
	children,
	...rest
}: NavLinkMainProps) {
	return (
		<Typography
			component="a"
			sx={combineSx(
				({ borderRadius, shadows, size, vars }) => ({
					overflow: 'hidden',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'stretch',
					color: vars.palette.text.primary,
					textDecoration: 'none',
					paddingY: { xs: 200, sm: 100 },
					paddingLeft: { xs: 200, sm: 150 },
					paddingRight: 50,
					height: size.md,
					borderRadius: borderRadius.roundedXS,
					gap: 100,
					transition: 'background-color 0.2s ease-out',

					':visited': {
						color: vars.palette.uc.text.main,
					},

					':hover, :active, :focus-visible': {
						backgroundColor: vars.palette.uc.bg.medium,
					},

					':focus-visible': {
						outline: 'none',
					},

					...(active && {
						outline: '1px solid',
						outlineColor: vars.palette.uc.highlight.border.main,
						outlineOffset: '-1px',
						boxShadow: shadows[1],
						backgroundColor: vars.palette.uc.bg.main,

						':focus-visible': {
							backgroundColor: vars.palette.uc.bg.medium,
							outline: '1px solid',
							outlineColor: vars.palette.uc.highlight.border.main,
							outlineOffset: '-1px',
							boxShadow: shadows[1],
						},
					}),
				}),
				sxProp,
			)}
			{...rest}
		>
			{startIcon && (
				<SvgIcon sx={{ height: '16px', width: '16px' }}>{startIcon}</SvgIcon>
			)}
			<Typography
				component="span"
				variant="label-lg"
				display="block"
				flex="0% 1 1"
				whiteSpace="nowrap"
			>
				{children}
			</Typography>
			{endAdornment}
		</Typography>
	);
}
export default NavLinkMain;
